/* Spellbound feature media video playback.

   Mounts each [data-spellbound-feature-media] root on DOMContentLoaded and
   shopify:section:load, and tears it down on shopify:section:unload.
   Feature cards and terms are CSS only and need no JS.

   Inside a root:
   - video[data-spellbound-video]   rendered by video_tag with preload="none"
                                    and native controls (the no-JS fallback)
   - [data-spellbound-playback]     corner play / pause button, unhidden here

   Behaviour:
   - Sources are parked in data-src until the video is within 300px of the
     viewport, then restored and loaded with preload="metadata".
   - Autoplays (muted, looped) when at least 15% visible; pauses when it
     scrolls away or the tab is hidden. A user pause from the corner
     button persists until they press play again.
   - prefers-reduced-motion, navigator.connection.saveData, a rejected
     play() or a missing IntersectionObserver fall back to native controls.
   - data-poster (custom poster image) replaces the preview-image poster.
*/
(() => {
  if (window.SpellboundFeature) {
    window.SpellboundFeature.mount(document);
    return;
  }

  const ROOT = '[data-spellbound-feature-media]';
  const instances = new WeakMap();
  const sectionNodes = new WeakMap();
  const mounted = new Set();
  const keyByRoot = new WeakMap();
  const assignedKeys = new Map();

  function on(target, type, handler, options) {
    if (!target) return function remove() {};
    if (typeof target.addEventListener === 'function') {
      target.addEventListener(type, handler, options);
      return function remove() {
        target.removeEventListener(type, handler, options);
      };
    }
    if (typeof target.addListener === 'function') {
      target.addListener(handler);
      return function remove() {
        target.removeListener(handler);
      };
    }
    return function remove() {};
  }

  function collect(scope) {
    const found = [];
    if (!scope) return found;
    if (scope.nodeType === 1 && scope.matches(ROOT)) found.push(scope);
    if (typeof scope.querySelectorAll === 'function') {
      scope.querySelectorAll(ROOT).forEach((node) => {
        if (found.indexOf(node) === -1) found.push(node);
      });
    }
    return found;
  }

  function sectionKey(root) {
    const cached = keyByRoot.get(root);
    if (cached) return cached;
    const wrapper =
      typeof root.closest === 'function'
        ? root.closest('[id^="shopify-section-"]')
        : null;
    const raw =
      root.getAttribute('data-section-id') ||
      (wrapper && wrapper.id.replace(/^shopify-section-/, '')) ||
      'spellbound-feature';
    const base =
      String(raw)
        .trim()
        .replace(/[^A-Za-z0-9_-]+/g, '-') || 'spellbound-feature';
    let key = base;
    let suffix = 2;
    while (assignedKeys.has(key) && assignedKeys.get(key) !== root) {
      key = `${base}-${suffix}`;
      suffix += 1;
    }
    assignedKeys.set(key, root);
    keyByRoot.set(root, key);
    return key;
  }

  function releaseKey(root) {
    const key = keyByRoot.get(root);
    if (key && assignedKeys.get(key) === root) assignedKeys.delete(key);
    keyByRoot.delete(root);
  }

  function isSafeMediaUrl(url) {
    if (typeof url !== 'string') return false;
    const value = url.trim();
    if (!value) return false;
    try {
      const parsed = new URL(value, window.location.href);
      return parsed.protocol === 'https:' || parsed.protocol === 'http:';
    } catch (error) {
      return false;
    }
  }

  function inViewport(element) {
    const rect = element.getBoundingClientRect();
    return (
      rect.bottom > 0 &&
      rect.right > 0 &&
      rect.top < window.innerHeight &&
      rect.left < window.innerWidth
    );
  }

  function mountMedia(root) {
    const video = root.querySelector('video[data-spellbound-video]');
    if (!video) return null;
    const button = root.querySelector('[data-spellbound-playback]');
    const key = sectionKey(root);
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const connection =
      navigator.connection ||
      navigator.mozConnection ||
      navigator.webkitConnection ||
      null;
    const hasObserver = 'IntersectionObserver' in window;
    const removals = [];
    let alive = true;
    let inView = false;
    let nearView = false;
    let attached = false;
    let autoplayBlocked = false;
    let userPaused = false;
    let loadObserver = null;
    let viewObserver = null;

    if (!video.id) video.id = `${key}-feature-video`;
    if (button) button.setAttribute('aria-controls', video.id);

    video.muted = true;
    video.defaultMuted = true;
    video.loop = true;
    video.playsInline = true;
    video.setAttribute('playsinline', '');
    video.autoplay = false;
    video.removeAttribute('autoplay');
    video.preload = 'none';

    const customPoster = video.getAttribute('data-poster');
    if (isSafeMediaUrl(customPoster)) video.poster = customPoster.trim();

    // Park every source in data-src so nothing downloads until needed.
    const sourceNodes = [video].concat(
      Array.from(video.querySelectorAll('source')),
    );
    let hadLiveSource = false;
    sourceNodes.forEach((node) => {
      const live = node.getAttribute('src');
      if (live) {
        if (!node.getAttribute('data-src')) node.setAttribute('data-src', live);
        node.removeAttribute('src');
        hadLiveSource = true;
      }
    });
    const hasSource = sourceNodes.some((node) =>
      isSafeMediaUrl(node.getAttribute('data-src')),
    );
    if (hadLiveSource && video.currentSrc) {
      try {
        video.load();
      } catch (error) {
        // Nothing to reset.
      }
    }
    if (!hasSource) {
      // Nothing playable: leave native controls for the browser to explain.
      if (button) button.hidden = true;
      return null;
    }

    function manualMode() {
      return (
        reducedMotion.matches ||
        Boolean(connection && connection.saveData) ||
        autoplayBlocked ||
        !hasObserver
      );
    }

    function attach() {
      if (attached) return;
      attached = true;
      sourceNodes.forEach((node) => {
        const parked = node.getAttribute('data-src');
        if (isSafeMediaUrl(parked)) node.setAttribute('src', parked.trim());
      });
      video.preload = manualMode() ? 'none' : 'metadata';
      try {
        video.load();
      } catch (error) {
        // The browser will surface playback errors through the element.
      }
    }

    function updateButton() {
      if (!button) return;
      const playing = !video.paused && !video.ended;
      const label = playing ? 'Pause video' : 'Play video';
      button.dataset.playing = String(playing);
      button.setAttribute('aria-label', label);
      button.setAttribute('title', label);
    }

    function pause() {
      if (!video.paused) video.pause();
    }

    function requestPlay() {
      const attempt = video.play();
      if (attempt && typeof attempt.catch === 'function') {
        attempt.catch((error) => {
          if (!alive) return;
          // A scroll or visibility pause can interrupt a pending play().
          if (error && error.name === 'AbortError') return;
          autoplayBlocked = true;
          sync();
        });
      }
    }

    function sync() {
      if (!alive) return;
      const manual = manualMode();
      video.controls = manual;
      if (button) button.hidden = manual;

      if (manual) {
        // Native Play needs a source; preload="none" keeps the poster until then.
        attach();
      } else if (nearView || inView) {
        attach();
      }

      if (!inView || document.hidden || userPaused) {
        pause();
        updateButton();
        return;
      }
      if (!manual && attached && video.paused) requestPlay();
      updateButton();
    }

    function onPreferenceChange() {
      if (!alive) return;
      if (manualMode()) pause();
      sync();
    }

    function onToggle() {
      if (!alive) return;
      if (video.paused) {
        userPaused = false;
        attach();
        requestPlay();
      } else {
        userPaused = true;
        pause();
      }
      updateButton();
    }

    function onPlay() {
      if (!alive) return;
      if (!manualMode() && (!inView || document.hidden)) {
        pause();
        return;
      }
      updateButton();
    }

    removals.push(on(reducedMotion, 'change', onPreferenceChange));
    if (connection) removals.push(on(connection, 'change', onPreferenceChange));
    removals.push(on(document, 'visibilitychange', sync));
    removals.push(on(video, 'play', onPlay));
    removals.push(on(video, 'pause', updateButton));
    removals.push(on(video, 'ended', updateButton));
    if (button) removals.push(on(button, 'click', onToggle));

    if (hasObserver) {
      loadObserver = new IntersectionObserver(
        (entries) => {
          if (!alive) return;
          if (!entries.some((entry) => entry.isIntersecting)) return;
          nearView = true;
          sync();
          if (loadObserver) loadObserver.disconnect();
          loadObserver = null;
        },
        { rootMargin: '300px 0px' },
      );
      viewObserver = new IntersectionObserver(
        (entries) => {
          if (!alive) return;
          inView = entries.some(
            (entry) => entry.isIntersecting && entry.intersectionRatio >= 0.15,
          );
          sync();
        },
        { threshold: [0, 0.15] },
      );
      loadObserver.observe(video);
      viewObserver.observe(video);
    } else {
      // Older browsers get native Play with simple visibility-based pausing.
      const updateView = () => {
        if (!alive) return;
        inView = inViewport(video);
        sync();
      };
      removals.push(on(window, 'scroll', updateView, { passive: true }));
      removals.push(on(window, 'resize', updateView));
      updateView();
    }

    sync();

    return function destroyMedia() {
      alive = false;
      removals.forEach((remove) => remove());
      if (loadObserver) loadObserver.disconnect();
      if (viewObserver) viewObserver.disconnect();
      loadObserver = null;
      viewObserver = null;
      pause();
    };
  }

  function destroyOne(root) {
    const cleanups = instances.get(root);
    if (!cleanups) return;
    instances.delete(root);
    mounted.delete(root);
    sectionNodes.delete(root);
    releaseKey(root);
    cleanups.forEach((cleanup) => cleanup());
  }

  function mountOne(root) {
    if (instances.has(root)) return;
    const cleanups = [];
    const cleanup = mountMedia(root);
    if (cleanup) cleanups.push(cleanup);
    if (!cleanups.length) {
      releaseKey(root);
      return;
    }
    instances.set(root, cleanups);
    mounted.add(root);
    const wrapper =
      (typeof root.closest === 'function' &&
        root.closest('[id^="shopify-section-"]')) ||
      root;
    sectionNodes.set(root, wrapper);
  }

  function mount(scope) {
    collect(scope || document).forEach(mountOne);
  }

  function destroy(scope) {
    if (
      !scope ||
      scope === document ||
      scope === document.documentElement ||
      scope === document.body
    ) {
      Array.from(mounted).forEach(destroyOne);
      return;
    }
    const targets = new Set(collect(scope));
    mounted.forEach((root) => {
      const wrapper = sectionNodes.get(root);
      if (
        root === scope ||
        wrapper === scope ||
        (typeof scope.contains === 'function' && scope.contains(root))
      ) {
        targets.add(root);
      }
    });
    targets.forEach(destroyOne);
  }

  document.addEventListener('shopify:section:load', (event) => {
    mount(event.target);
  });
  document.addEventListener('shopify:section:unload', (event) => {
    destroy(event.target);
  });

  window.SpellboundFeature = { mount, destroy };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => mount(document), {
      once: true,
    });
  } else {
    mount(document);
  }
})();
